import React from 'react';
import { createSelector } from 'reselect';
import { Link, withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { map, find, filter, get } from 'lodash-es';
import { navigationItemModel } from 'common/api/models/index';
import { isIframe } from 'integration/integration.utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { collectionSelector, channelsSelector, integrationSelector } from 'common/selectors';

const DesktopBreadcrumbs = () => {
  const breadcrumbs = useSelector(breadcrumbsSelector);
  const integration = useSelector(integrationSelector);
  const showSubChannels = !isIframe(integration);
  const { t } = useTranslation();

  return (
    breadcrumbs && (
      <nav aria-label={t('breadcrumbs_navigation.aria_label')} className="ct-breadcrumb" data-e2e="breadcrumb">
        <ol>
          {map(breadcrumbs.parents, ({ id, title, url, parentId }) => {
            return parentId ? (
              <li key={id}>
                <Link to={url} data-e2e="breadcrumb-prev" className="ct-breadcrumb__prev">
                  {title}
                  <FontAwesomeIcon icon={faAngleRight} />
                </Link>
              </li>
            ) : (
              <li key={id}>
                <Link to="/" data-e2e="breadcrumb-prev" className="ct-breadcrumb__prev">
                  {title}
                  <FontAwesomeIcon icon={faAngleRight} />
                </Link>
              </li>
            );
          })}

          <li className="ct-breadcrumb__current-wrapper">
            <div aria-current="page" className="ct-breadcrumb__current" data-e2e="breadcrumb-current">
              {breadcrumbs.current.title}
            </div>

            {showSubChannels && breadcrumbs.children?.length > 0 && (
              <ol>
                {map(breadcrumbs.children, ({ id, title, url }) => (
                  <li key={id}>
                    <Link to={url} data-e2e="breadcrumb-next" className="ct-breadcrumb__next">
                      <FontAwesomeIcon icon={faAngleRight} />
                      {title}
                    </Link>
                  </li>
                ))}
              </ol>
            )}
          </li>
        </ol>
      </nav>
    )
  );
};

const getItemById = (collection, itemId) => (!isNaN(itemId) ? find(collection, ({ id }) => id === itemId) : null);

const getParents = (navigation, current) => {
  const parents = [getItemById(navigation, get(current, 'parentId'))];
  while (get(parents, '[0].parentId')) {
    parents.unshift(getItemById(navigation, get(parents, '[0].parentId'))); // eslint-disable-line no-loop-func
  }
  return parents;
};

const breadcrumbsSelector = createSelector(collectionSelector, channelsSelector, (collection, channels) => {
  const navigationItems = channels.map(navigationItemModel);
  const currentChannel = getItemById(navigationItems, collection.channelId);
  const currentChannelId = get(currentChannel, 'id');

  if (isNaN(currentChannelId) || currentChannelId === -1) {
    return null;
  }

  return {
    parents: getParents(navigationItems, currentChannel),
    current: currentChannel,
    children: filter(navigationItems, ({ parentId }) => parentId === get(currentChannel, 'id')),
  };
});

export const DesktopBreadcrumbsDecorated = withRouter(DesktopBreadcrumbs);
